import { useCallback, useEffect, useState } from "react";
import { Flex } from "@mantine/core";
import { useNavigate } from "react-router";
import styled from "styled-components";
import { ArrowPathIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { BoxTitle, Container } from "./StyledComponents";
import { theme } from "../../theme";
import {
  RotationCell,
  getRotationPreview,
  getShiftRotations,
  getShiftTeams,
} from "../../api/shiftRotationApi";
import { todayKey } from "../../utils/shiftPattern";
import { useAuth } from "../../context/AuthContext";

/**
 * BUGÜN HANGİ EKİP NEREDE — rotanın ana sayfadaki özeti (plan 2.62).
 *
 * ⚠ NEDEN AYRI BİR WIDGET (kullanıcı kararı, 7 Eyl): "Bugünün Vardiyaları"na
 * eklemek konuşuldu ve VAZGEÇİLDİ. O kart takvimden atanan **somut** vardiyaları
 * gösteriyor (`shifts` koleksiyonu, gerçekleşen kayıt); rota ise **hesaplanan**
 * bir şey — hiçbir yerde "A ekibi 24 Ağustos'ta gece" diye kayıt yok. İkisini
 * tek kutuya sıkıştırmak ikisini de bozardı.
 *
 * ⚠ ÖZET, KOPYA DEĞİL. Tam matris [`RotationBoard`](../../pages/RotationBoard.tsx)'da
 * ve orası bilerek ayrı bir "bakma ekranı" olarak kurulmuştu (plan 2.34).
 * Matrisi burada tekrarlamak iki yerde bakım demek olurdu; bu kutu yalnızca
 * BUGÜNÜN dönemini yazıp oraya gönderiyor.
 *
 * ⚠ HESAP BURADA YOK. Dönem/ekip eşlemesi `/preview` ucundan hazır geliyor
 * (21 Ağu kararı, bkz. `shiftRotationApi.tsx`): modulo/takvim matematiğini
 * panelde ikinci kez yazmak, biri düzeltilip diğeri unutulduğunda ekranı
 * puantajdan sessizce ayırırdı.
 *
 * ⚠ BOŞLUĞUN SEBEBİ YAZILIYOR (2.60'ın dersi): "rota tanımlı değil" ile
 * "bugün rota döneminin dışında" farklı haberler; sessiz boş kutu ikisini de
 * arıza gibi gösteriyordu.
 */

/** Aynı anda özetlenecek en fazla rota — her biri bir `/preview` isteği. */
const AZAMI_ROTA = 4;

type Satir = { rotaAdi: string; donem: string; hucreler: RotationCell[] };

const RotationToday = ({ flex }: { flex: any }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [satirlar, setSatirlar] = useState<Satir[]>([]);
  const [uyeSayisi, setUyeSayisi] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [rotaVarMi, setRotaVarMi] = useState(true);

  const yukle = useCallback(async () => {
    setLoading(true);
    try {
      const bugun = todayKey();
      const [rotaYanit, ekipYanit]: any[] = await Promise.all([
        getShiftRotations({ limit: 20 }),
        getShiftTeams({ limit: 100 }),
      ]);

      const ekipler = ekipYanit?.data ?? [];
      setUyeSayisi(
        Object.fromEntries(ekipler.map((e: any) => [e._id, e.memberCount ?? 0]))
      );

      const rotalar = (rotaYanit?.data ?? []).slice(0, AZAMI_ROTA);
      setRotaVarMi(rotalar.length > 0);

      const sonuc: Satir[] = [];
      for (const rota of rotalar) {
        const onizleme: any = await getRotationPreview(rota._id, bugun, 1);
        // `from=bugün` verilse de dönem bugünden ÖNCE başlamış olabilir
        // (haftalık çevrimde Pazartesi); bugünü İÇEREN dönem aranıyor.
        const donem = (onizleme?.data?.periods ?? []).find(
          (p: any) => p.startDate <= bugun && bugun <= p.endDate
        );
        if (!donem) continue;
        sonuc.push({
          rotaAdi: rota.name,
          donem: `${donem.startDate} → ${donem.endDate}`,
          hucreler: donem.cells ?? [],
        });
      }
      setSatirlar(sonuc);
    } catch (error) {
      // Sessiz geç: bu kutu bir özet, sayfanın ön koşulu değil.
      console.error("Rota özeti alınamadı:", error);
      setSatirlar([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    yukle();
  }, [yukle]);

  const tekRota = satirlar.length === 1;

  return (
    <Container $flex={flex}>
      <HeaderRow>
        <Flex align="center" gap={10}>
          <HeaderIcon>
            <ArrowPathIcon width={18} />
          </HeaderIcon>
          <div>
            <BoxTitle style={{ fontSize: "17px", fontWeight: 600 }}>
              Bugün Hangi Ekip Nerede
            </BoxTitle>
            {tekRota && (
              <AltBilgi>
                {satirlar[0].rotaAdi} · {tarihAralik(satirlar[0].donem)}
              </AltBilgi>
            )}
          </div>
        </Flex>
        <SeeAll onClick={() => navigate("/shifts/rotation-board")}>
          Tümünü gör
          <ChevronRightIcon width={16} />
        </SeeAll>
      </HeaderRow>

      {loading ? (
        <Bos>Yükleniyor…</Bos>
      ) : !rotaVarMi ? (
        <Bos>
          Tanımlı rota yok.
          {user?.privilege === "admin" && (
            <BosBaglanti onClick={() => navigate("/shifts/rotations")}>
              Rota tanımla
            </BosBaglanti>
          )}
        </Bos>
      ) : satirlar.length === 0 ? (
        /* Rota VAR ama bugün hiçbirinin dönemine düşmüyor — başlangıç
           tarihi ileride ya da rota bitmiş olabilir. Sessiz boş kutu bunu
           arıza gibi gösteriyordu. */
        <Bos>Bugün yürürlükte bir rota dönemi yok.</Bos>
      ) : (
        <Liste>
          {satirlar.map((s, i) => (
            <RotaBlok key={i}>
              {!tekRota && (
                <RotaBaslik>
                  {s.rotaAdi} · {tarihAralik(s.donem)}
                </RotaBaslik>
              )}
              {/*
                Izgara, satır DEĞİL: kutu tam genişlikte duruyor ve tek satırda
                ekip adı solda, vardiya pili sağ kenarda kalınca aralarında
                okunamayacak kadar boşluk oluşuyordu. Kart düzeni genişliği
                kullanıyor ve ad ile vardiyayı yan yana tutuyor.
              */}
              <EkipIzgara>
                {s.hucreler.map((h) => (
                  <EkipKart key={h.teamId} $dinlenme={!h.shiftName}>
                    <EkipUst>
                      <EkipAd title={h.teamName}>{h.teamName}</EkipAd>
                      <UyeRozet>{uyeSayisi[h.teamId] ?? 0} kişi</UyeRozet>
                    </EkipUst>
                    {h.shiftName ? (
                      <VardiyaPil>{h.shiftName}</VardiyaPil>
                    ) : (
                      /* `shiftDefinitionId: null` = o dönem o ekip DİNLENİR.
                         Boş bırakmak "veri gelmedi" gibi okunurdu. */
                      <DinlenmePil>Dinlenme</DinlenmePil>
                    )}
                  </EkipKart>
                ))}
              </EkipIzgara>
            </RotaBlok>
          ))}
        </Liste>
      )}
    </Container>
  );
};

/** "2026-09-07 → 2026-09-13" → "7 – 13 Eyl" */
const AYLAR = ["Oca","Şub","Mar","Nis","May","Haz","Tem","Ağu","Eyl","Eki","Kas","Ara"];
const tarihAralik = (aralik: string) => {
  const [bas, bit] = aralik.split(" → ");
  if (!bas || !bit) return aralik;
  const [, ay1, g1] = bas.split("-");
  const [, ay2, g2] = bit.split("-");
  const sol = ay1 === ay2 ? `${Number(g1)}` : `${Number(g1)} ${AYLAR[Number(ay1) - 1]}`;
  return `${sol} – ${Number(g2)} ${AYLAR[Number(ay2) - 1]}`;
};

export default RotationToday;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;
`;

const HeaderIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${theme.colors.brand.normal}15;
  color: ${theme.colors.brand.normal};
  border: 1px solid ${theme.colors.brand.normal}20;
`;

const AltBilgi = styled.div`
  margin-top: 2px;
  font-size: 12px;
  /* Sıkı satır yüksekliği Türkçe büyük harflerin tepesini kırpıyor. */
  line-height: 17px;
  color: ${theme.colors.greyscale.gray80};
`;

const SeeAll = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: transparent;
  padding: 6px 8px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: ${theme.colors.brand.normal};
  flex-shrink: 0;
  transition: background 0.2s ease;
  &:hover {
    background: ${theme.colors.brand.normal}12;
  }
`;

const Liste = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const RotaBlok = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const RotaBaslik = styled.div`
  font-size: 12px;
  font-weight: 700;
  line-height: 17px;
  color: ${theme.colors.greyscale.gray80};
  margin-bottom: 4px;
`;

const EkipIzgara = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 10px;
`;

const EkipKart = styled.div<{ $dinlenme: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid ${theme.colors.greyscale.gray20};
  /* Dinlenen ekip soluk zeminde: renk tek başına da bilgi taşısın. */
  background: ${(p) =>
    p.$dinlenme ? theme.colors.greyscale.gray00 : theme.colors.greyscale.white};
  /* Grid çocuğu varsayılan min-width:auto ile içeriğinin altına inemiyor;
     uzun ekip adı sütunu şişiriyordu. */
  min-width: 0;
`;

const EkipUst = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const EkipAd = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: ${theme.colors.greyscale.black};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const UyeRozet = styled.span`
  flex-shrink: 0;
  padding: 1px 8px;
  border-radius: 50px;
  background: ${theme.colors.greyscale.gray10};
  color: ${theme.colors.greyscale.gray80};
  font-size: 11px;
  font-weight: 600;
  line-height: 17px;
`;

const VardiyaPil = styled.span`
  align-self: flex-start;
  padding: 3px 12px;
  border-radius: 50px;
  background: ${theme.colors.brand.normal}12;
  color: ${theme.colors.brand.normal};
  border: 1px solid ${theme.colors.brand.normal}25;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 18px;
`;

const DinlenmePil = styled(VardiyaPil)`
  background: ${theme.colors.greyscale.gray10};
  color: ${theme.colors.greyscale.gray60};
  border-color: ${theme.colors.greyscale.gray20};
`;

const Bos = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  font-size: 13.5px;
  line-height: 19px;
  color: ${theme.colors.greyscale.gray60};
`;

const BosBaglanti = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${theme.colors.brand.normal};
  cursor: pointer;
`;
